import React from "react";
import {
  Box,
  VStack,
  HStack,
  Input,
  Textarea,
  Select,
  FormControl,
  FormLabel,
  Checkbox,
  Stack,
  Heading,
  Switch,
  Button,
  Flex,
  Text,
} from "@chakra-ui/react";
import { FiBook } from "react-icons/fi";
import { ToolConfig, AgentConfig } from "build-ts/agent_config";
import { AgentType } from "build-ts/agent";

interface EvaLeftPanelProps {
  agentConfig: AgentConfig;
  availableTools: ToolConfig[];
  onConfigChange: (field: string, value: any) => void;
  onToolsChange: (selectedToolsArray: ToolConfig[]) => void;
  isEditing?: boolean;
  onEdit?: () => void;
  onCancel?: () => void;
  onSave: () => void;
  isCreateMode?: boolean;
  onViewDocs?: () => void;
}

const EvaLeftPanel: React.FC<EvaLeftPanelProps> = ({
  agentConfig,
  availableTools,
  onConfigChange,
  onToolsChange,
  isEditing = false,
  onEdit,
  onCancel,
  onSave,
  isCreateMode = false,
  onViewDocs,
}) => {

  return (
    <Box className="eva-left-panel eva-small-scrollbar">
      <VStack spacing={6} align="stretch">
        {/* Basic Agent Info */}
        <Box className="eva-section-card">
          <Flex justify="space-between" align="center" mb={4}>
            <Heading size="xs" color="gray.700" mb={0}>
              Agent Config
            </Heading>
            {isCreateMode ? (
                <HStack spacing={2}>
                  <Button size="sm" variant="outline" onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="green"
                    onClick={onSave}
                  >
                    Create Agent
                  </Button>
                </HStack>
              ) : !isEditing ? (
                <Button size="sm" colorScheme="blue" onClick={onEdit}>
                  Edit Agent
                </Button>
              ) : (
                <HStack spacing={2}>
                  <Button size="sm" variant="outline" onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="green"
                    onClick={onSave}
                  >
                    Save
                  </Button>
                </HStack>
              )}
          </Flex>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>Agent Name</FormLabel>
              <Input
                value={agentConfig.name}
                onChange={(e) => onConfigChange("name", e.target.value)}
                placeholder="Enter agent name"
                isDisabled={!isEditing && !isCreateMode}
                color={!isEditing && !isCreateMode ? "gray.400" : "black"}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={agentConfig.description}
                onChange={(e) => onConfigChange("description", e.target.value)}
                placeholder="Describe the agent's purpose and notes"
                rows={5}
                isDisabled={!isEditing && !isCreateMode}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Agent Type</FormLabel>
              {isCreateMode ? (
                <Select
                  value={agentConfig.agent_type}
                  onChange={(e) => onConfigChange("agent_type", e.target.value)}
                  placeholder="Select agent type"
                >
                  {Object.values(AgentType)
                    .filter(
                      value => typeof value === "string" && value !== "UNRECOGNIZED" && value !== "UNKNOWN_AGENT_TYPE"
                    )
                    .map((agentType) => (
                      <option key={agentType} value={agentType}>
                        {agentType}
                      </option>
                    ))}
                </Select>
              ) : (
                <Input value={agentConfig.agent_type} readOnly bg="gray.50" color="gray.400" />
              )}
            </FormControl>

            <FormControl>
              <FormLabel>
                Is Agent Active
                <Switch
                  isChecked={agentConfig?.is_active}
                  onChange={(e) => onConfigChange("is_active", e.target.checked)}
                  colorScheme="blue"
                  margin={3}
                  isDisabled={!isEditing && !isCreateMode}
                />
              </FormLabel>
            </FormControl>

            <FormControl>
              <FormLabel>Analysis Flag Configuration</FormLabel>
              <Textarea
                value={agentConfig.analysis_flag || ""}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  try {
                    // Try to parse as JSON
                    const parsed = JSON.parse(inputValue);
                    onConfigChange("analysis_flag", parsed);
                  } catch (error) {
                    // If JSON is invalid, store the raw input for user to fix
                    onConfigChange("analysis_flag", inputValue);
                  }
                }}
                placeholder={"{\"include_tree_insights_in_analysis\": false, \"include_tree_info\": false}"}
                rows={4}
                fontFamily="mono"
                fontSize="sm"
                isDisabled={!isEditing && !isCreateMode}
              />
            </FormControl>
          </VStack>

          {/* Tools Selection */}
          <Heading size="xs" color="gray.700" mb={4} mt={4}>
            Available Tools
          </Heading>
          <Stack spacing={2}>
            {availableTools.map((tool) => {
              const isChecked = agentConfig.tools?.some(selectedTool => selectedTool.id === tool.id) || false;
              return (
                <Checkbox
                  key={tool.id}
                  value={tool.id}
                  isChecked={isChecked}
                  colorScheme="blue"
                  size="md"
                  isDisabled={!isEditing && !isCreateMode}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    const currentTools = agentConfig.tools || [];
                    if (isChecked) {
                      onToolsChange([...currentTools, tool]);
                    } else {
                      onToolsChange(currentTools.filter(currentTool => currentTool.id !== tool.id));
                    }
                  }}
                >
                  {tool.name}
                </Checkbox>
              );
            })}
          </Stack>
        </Box>

        {/* Documentation Section */}
        <Box className="eva-section-card">
          <Flex justify="space-between" align="center" mb={4}>
            <Heading size="xs" color="gray.700">
              Documentation
            </Heading>
          </Flex>
          <VStack spacing={3} align="stretch">
            <Text fontSize="sm" color="gray.600" mb={2}>
              Access system prompt variable documentation.
            </Text>
            <Button
              leftIcon={<FiBook />}
              size="sm"
              variant="outline"
              onClick={onViewDocs}
              colorScheme="blue"
              borderColor="blue.300"
              _hover={{ borderColor: "blue.400", bg: "blue.50" }}
            >
              View Variable Docs
            </Button>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
};

export default EvaLeftPanel;
